<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import { LoadingState } from "@/components/episto-ui/loading-state";
  import OnePanePage from "@/templates/OnePanePage.vue";
  import { useChatPagesStore, type ChatPageCustomisation } from "@stores/chatPages";
  import { getChatAppBaseUrl } from "@utils/constants/chatUrls";
  import { CHAT_PAGE_PREVIEW_PARAMS, CHAT_PAGE_THEME_MESSAGE } from "@utils/constants/chatPages";

  const route = useRoute();
  const router = useRouter();
  const chatPagesStore = useChatPagesStore();
  const { customizedChatPage, previewCustomisation } = storeToRefs(chatPagesStore);

  const token = computed(() => route.params.token as string);
  const loading = ref(true);

  const chatAppOrigin = computed(() => new URL(getChatAppBaseUrl(), window.location.origin).origin);

  const customizerSrc = computed(() => {
    if (!customizedChatPage.value) return "";

    const params = new URLSearchParams({ p: customizedChatPage.value.token, ...CHAT_PAGE_PREVIEW_PARAMS });
    return `${getChatAppBaseUrl()}/?${params}`;
  });

  // Reset means "back to the last saved state": remounting reloads the theme
  // the API holds, which is exactly that.
  const { reloadKey } = storeToRefs(chatPagesStore);

  const receiveTheme = (event: MessageEvent) => {
    if (event.origin !== chatAppOrigin.value) return;
    if (event.data?.type !== CHAT_PAGE_THEME_MESSAGE) return;

    previewCustomisation.value = event.data.customisation as ChatPageCustomisation;
  };

  onMounted(async () => {
    window.addEventListener("message", receiveTheme);
    const chatPage = await chatPagesStore.fetchChatPageByToken(token.value);
    loading.value = false;
    if (!chatPage) router.replace({ name: "ChatPagesIndex" });
  });

  onUnmounted(() => {
    window.removeEventListener("message", receiveTheme);
    chatPagesStore.resetCustomizer();
  });
</script>

<template>
  <OnePanePage data-cy="chat-page-customizer">
    <LoadingState
      v-if="loading"
      class="py-16" />

    <iframe
      v-else-if="customizedChatPage"
      :key="reloadKey"
      :src="customizerSrc"
      title="Chat page customizer"
      allow="camera *; microphone *"
      class="h-[calc(100vh-100px)] w-full rounded-lg border-none bg-white" />
  </OnePanePage>
</template>
